import React from "react";

import { Flex } from "@magnetic/flex";
import { Negative } from "@magnetic/icons";
import { Input, InputProps } from "@magnetic/input";
import { Tooltip } from "@magnetic/tooltip";

import { FieldError } from "src/core/apiError";

import "./input.scss";

export enum IPAddressType {
  IPV4 = "IPv4",
  IPV6 = "IPv6"
}

type InputFieldProps = {
  readonly value: string;
  readonly errorMap?: Map<string, FieldError>;
  readonly name: string;
} & Omit<InputProps, "ref">;

const InputComponent = (props: InputFieldProps) => {
  const { errorMap, type, name } = props;
  const errorMessage = name ? errorMap?.get(name)?.message : undefined;
  const Comp = type === "password" ? Input.Password : Input;

  return (
    <Flex direction="vertical" gap={8} grow={1}>
      <Comp
        {...props}
        invalid={!!errorMessage}
        outsideSuffix={
          !!errorMessage ? (
            <Tooltip title={errorMessage}>
              <Negative
                data-qa="field-error-indicator"
                height={24}
                width={24}
              />
            </Tooltip>
          ) : null
        }
      />
    </Flex>
  );
};

export const InputIP = (props: InputFieldProps) => {
  return <InputComponent {...props} type="text" />;
};

export const InputURL = (props: InputFieldProps) => {
  return <InputComponent {...props} type="url" />;
};

export const InputText = (props: InputFieldProps) => {
  return <InputComponent {...props} type="text" />;
};

const updateEventTargetValueWith = (
  e:
    | React.KeyboardEvent<HTMLInputElement>
    | React.ChangeEvent<HTMLInputElement>,
  value: string = ""
) => {
  return {
    ...e,
    target: {
      ...e.target,
      value
    }
  };
};

export const InputPassword = (
  props: InputFieldProps & {
    readonly initialValue: string;
  }
) => {
  const { initialValue, value } = props;
  return (
    <InputComponent
      {...props}
      type="password"
      onKeyDown={(e) => {
        // empty the password field if backspace is pressed
        if (e.key === "Backspace" && initialValue === value) {
          const updatedEvent = updateEventTargetValueWith(
            e,
            ""
          ) as React.ChangeEvent<HTMLInputElement>;
          props?.onChange?.(updatedEvent);
        }
        const updatedEvent = updateEventTargetValueWith(
          e,
          ""
        ) as React.KeyboardEvent<HTMLInputElement>;
        props?.onKeyDown?.(updatedEvent);
      }}
      onChange={(e) => {
        let updatedEvent;
        const val = e.target.value;
        // Adding to the password string
        if (val.includes(initialValue)) {
          updatedEvent = updateEventTargetValueWith(
            e,
            // empty the password and take the new value
            val.replace(initialValue, "")
          ) as React.ChangeEvent<HTMLInputElement>;
        } else {
          updatedEvent = updateEventTargetValueWith(
            e,
            val
          ) as React.ChangeEvent<HTMLInputElement>;
        }
        props?.onChange?.(updatedEvent);
      }}
    />
  );
};
